// src/application-steps/StepContent2.js

import React, { memo } from "react";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";

import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";

import FormRow from "../../helpers/FormRow";
import FormInput from "../../helpers/FormInput";
import FormCheckbox from "../../helpers/FormCheckbox";
import FormSelect from "../../helpers/FormSelect";
import FormRadioGroup from "../../helpers/FormRadioGroup";
import FormGroupComponent from "../../helpers/FormGroup";
import { calculateScholarshipYear } from "../../utils/dateHelpers";

function StepContent2({
  formData,
  setFormData,
  stepErrors,
  handleChange,
  isReadOnly,
  scholarshipYear,
}) {
  const currentScholarshipYear = scholarshipYear || calculateScholarshipYear();
  const nextYear = Number(currentScholarshipYear) + 1;
  const getStatus = (field, isRequired) => {
    if (stepErrors[field]) {
      return "error";
    }
    if (isRequired && formData[field]) {
      return "success";
    }
    if (!isRequired && !formData[field]) {
      return "warning";
    }
    return "";
  };

  const toggleBool = (field) => (e) => {
    const checked = e?.target?.checked ?? false;
    setFormData((prev) => ({ ...prev, [field]: checked }));
  };

  const otherSelected = Boolean(
    formData?.PS_OTHER_SELECTED || (formData?.PS_Other ?? "").trim(),
  );

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, sm: 3 },
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
        bgcolor: "background.paper",
      }}
      className="form-group"
    >
      {/* Header */}
      <Stack spacing={1} sx={{ mb: 2 }}>
        <Typography variant="h5" fontWeight={800} textAlign="center">
          Career and Post-Secondary Information:
        </Typography>
      </Stack>

      <Divider sx={{ my: 3 }} />

      {/* Career direction */}
      <Stack spacing={2}>
        <FormRow spacing={2}>
          <Grid item xs={12} md={6}>
            <FormGroupComponent
              label="What is your Career/Job Direction?"
              className="row"
            >
              <FormInput
                label="What is your Career/Job Direction?"
                id="careerJobDirection"
                value={formData.careerJobDirection}
                onChange={handleChange("careerJobDirection")}
                required
                disabled={isReadOnly}
                error={Boolean(stepErrors.careerJobDirection)}
                helperText={stepErrors.careerJobDirection || ""}
                status={getStatus("careerJobDirection", true)}
              />
            </FormGroupComponent>
          </Grid>
        </FormRow>

        <Divider />

        {/* Direct to post-secondary */}
        <FormRow spacing={2}>
          <Grid item xs={12}>
            <FormRadioGroup
              label={`Are you attending a post-secondary institute in ${nextYear}?`}
              name="directToPostSecondary"
              value={formData.directToPostSecondary}
              onChange={handleChange("directToPostSecondary")}
              options={[
                { value: "Yes", label: "Yes" },
                { value: "No", label: "No" },
              ]}
              required
              row
              isReadOnly={isReadOnly}
              error={Boolean(stepErrors.directToPostSecondary)}
              helperText={stepErrors.directToPostSecondary || ""}
              status={getStatus("directToPostSecondary", true)}
            />
          </Grid>

          {formData.directToPostSecondary === "No" && (
            <Grid item xs={12} md={6}>
              <FormInput
                label="If No, WHEN?"
                id="postSecondaryWhen"
                value={formData.postSecondaryWhen}
                onChange={handleChange("postSecondaryWhen")}
                required
                disabled={isReadOnly}
                error={Boolean(stepErrors.postSecondaryWhen)}
                helperText={stepErrors.postSecondaryWhen || ""}
                status={getStatus(
                  "postSecondaryWhen",
                  formData.directToPostSecondary === "No",
                )}
              />
            </Grid>
          )}
        </FormRow>

        {/* Schools applied to */}
        {formData.directToPostSecondary === "Yes" && (
          <>
            <Divider />

            <Grid item xs={12}>
              <FormGroupComponent
                label="Which post-secondary schools have you applied to?"
                className="form-group-row"
              >
                <Grid container spacing={1}>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormCheckbox
                      label="VIU"
                      checked={Boolean(formData?.PS_VIU)}
                      onChange={toggleBool("PS_VIU")}
                      name="PS_VIU"
                      isReadOnly={isReadOnly}
                      displaySide="left"
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <FormCheckbox
                      label="UBC"
                      checked={Boolean(formData?.PS_UBC)}
                      onChange={toggleBool("PS_UBC")}
                      name="PS_UBC"
                      isReadOnly={isReadOnly}
                      displaySide="left"
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <FormCheckbox
                      label="UVic"
                      checked={Boolean(formData?.PS_UVIC)}
                      onChange={toggleBool("PS_UVIC")}
                      name="PS_UVIC"
                      isReadOnly={isReadOnly}
                      displaySide="left"
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <FormCheckbox
                      label="SFU"
                      checked={Boolean(formData?.PS_SFU)}
                      onChange={toggleBool("PS_SFU")}
                      name="PS_SFU"
                      isReadOnly={isReadOnly}
                      displaySide="left"
                    />
                  </Grid>

                  {/* OTHER */}
                  <Grid item xs={12} sm={6} md={3}>
                    <FormCheckbox
                      label="Other"
                      checked={otherSelected}
                      onChange={(e) => {
                        const checked = e?.target?.checked ?? false;

                        setFormData((prev) => ({
                          ...prev,
                          PS_OTHER_SELECTED: checked,
                          PS_Other: checked ? prev.PS_Other || "" : "",
                        }));
                      }}
                      name="PS_OTHER_SELECTED"
                      isReadOnly={isReadOnly}
                      displaySide="left"
                    />
                  </Grid>

                  {otherSelected && (
                    <Grid item xs={12} md={8}>
                      <FormInput
                        label="Other post-secondary school(s)"
                        id="PS_Other"
                        value={formData.PS_Other}
                        onChange={(e) => {
                          const value = e?.target?.value ?? "";
                          setFormData((prev) => ({
                            ...prev,
                            PS_Other: value,
                            PS_OTHER_SELECTED: Boolean(value.trim()),
                          }));
                        }}
                        required
                        disabled={isReadOnly}
                        error={Boolean(stepErrors.PS_Other)}
                        helperText={stepErrors.PS_Other || ""}
                        status={getStatus("PS_Other", true)}
                      />
                    </Grid>
                  )}
                </Grid>
              </FormGroupComponent>
            </Grid>
          </>
        )}

        <Divider />

        {/* Been Accepted */}
        <FormRow spacing={2}>
          <Grid item xs={12} md={6}>
            <FormCheckbox
              label="Have you been accepted to a post-secondary institution?"
              checked={Boolean(formData?.beenAccepted)}
              onChange={toggleBool("beenAccepted")}
              name="beenAccepted"
              isReadOnly={isReadOnly}
              displaySide="left"
            />
          </Grid>
        </FormRow>

        <Divider />

        {/* PS Path */}
        <FormRow spacing={2}>
          <Grid item xs={12}>
            <FormRadioGroup
              name="PS_Path"
              label="What do you plan to study at post-secondary:"
              value={formData.PS_Path}
              onChange={handleChange("PS_Path")}
              options={[
                { value: "Degree", label: "Degree" },
                { value: "Diploma", label: "Diploma" },
                { value: "Certificate", label: "Certificate" },
                { value: "Other", label: "Other" },
              ]}
              row
              isReadOnly={isReadOnly}
              error={Boolean(stepErrors.PS_Path)}
              helperText={stepErrors.PS_Path || ""}
              status={getStatus("PS_Path", true)}
            />
          </Grid>
        </FormRow>

        {formData.PS_Path === "Other" && (
          <FormRow spacing={2}>
            <Grid item xs={12} md={6}>
              <FormGroupComponent label="Other (Program Type):">
                <FormInput
                  label="Other (Program Type)"
                  id="PS_Path_Other"
                  value={formData.PS_Path_Other}
                  onChange={handleChange("PS_Path_Other")}
                  required
                  disabled={isReadOnly}
                  error={Boolean(stepErrors.PS_Path_Other)}
                  helperText={stepErrors.PS_Path_Other || ""}
                  status={getStatus(
                    "PS_Path_Other",
                    formData.PS_Path === "Other",
                  )}
                />
              </FormGroupComponent>
            </Grid>
          </FormRow>
        )}

        <Divider />

        {/* Field of study prompt */}
        <Typography variant="body1" fontWeight={800}>
          A number of awards are for specific fields of study. Which field of
          study are you pursuing?
        </Typography>

        <FormRow spacing={2}>
          <Grid item xs={12}>
            <FormGroupComponent
              label="Field of Study:"
              className="form-group-row"
            >
              <FormSelect
                label="Field of Study"
                id="fieldOfStudy"
                value={formData.fieldOfStudy}
                onChange={handleChange("fieldOfStudy")}
                disabled={isReadOnly}
                error={Boolean(stepErrors.fieldOfStudy)}
                helperText={stepErrors.fieldOfStudy || ""}
                status={getStatus("fieldOfStudy", true)}
              >
                <MenuItem value="">
                  <em>Select Field of Study</em>
                </MenuItem>
                <MenuItem value="Aesthetics">Aesthetics</MenuItem>
                <MenuItem value="Animal Sciences">Animal Sciences</MenuItem>
                <MenuItem value="Automotive">Automotive</MenuItem>
                <MenuItem value="Aviation">Aviation</MenuItem>
                <MenuItem value="Benchwork & Joinery">
                  Benchwork & Joinery
                </MenuItem>
                <MenuItem value="Broadcasting">Broadcasting</MenuItem>
                <MenuItem value="Business">Business</MenuItem>
                <MenuItem value="Carpentry">Carpentry</MenuItem>
                <MenuItem value="Computer">Computer</MenuItem>
                <MenuItem value="Communications">Communications</MenuItem>
                <MenuItem value="Culinary Arts">Culinary Arts</MenuItem>
                <MenuItem value="Dentistry">Dentistry</MenuItem>
                <MenuItem value="Drama Arts Music">Drama Arts Music</MenuItem>
                <MenuItem value="Education">Education</MenuItem>
                <MenuItem value="Emergency Services">
                  Emergency Services
                </MenuItem>
                <MenuItem value="Engineering">Engineering</MenuItem>
                <MenuItem value="Engineering (Chemistry or Environmental)">
                  Engineering (Chemistry or Environmental)
                </MenuItem>
                <MenuItem value="Engineering or Medical">
                  Engineering or Medical
                </MenuItem>
                <MenuItem value="Environmental">Environmental</MenuItem>
                <MenuItem value="Environmental, Fisheries, Forestry">
                  Environmental, Fisheries, Forestry
                </MenuItem>
                <MenuItem value="Environmental, Horticulture, Agriculture">
                  Environmental, Horticulture, Agriculture
                </MenuItem>
                <MenuItem value="Fine Arts">Fine Arts</MenuItem>
                <MenuItem value="Forestry">Forestry</MenuItem>
                <MenuItem value="Health Care">Health Care</MenuItem>
                <MenuItem value="History">History</MenuItem>
                <MenuItem value="Information Technology">
                  Information Technology
                </MenuItem>
                <MenuItem value="Medical">Medical</MenuItem>
                <MenuItem value="Military">Military</MenuItem>
                <MenuItem value="Music">Music</MenuItem>
                <MenuItem value="Recreation or P.E Teacher">
                  Recreation or P.E Teacher
                </MenuItem>
                <MenuItem value="Registered Nursing">
                  Registered Nursing
                </MenuItem>
                <MenuItem value="Social Services">Social Services</MenuItem>
                <MenuItem value="Technical or Vocational">
                  Technical or Vocational
                </MenuItem>
                <MenuItem value="Textile Arts">Textile Arts</MenuItem>
                <MenuItem value="Tourism">Tourism</MenuItem>
                <MenuItem value="Trades">Trades</MenuItem>
                <MenuItem value="Transportation (driver, pilot etc.)">
                  Transportation (driver, pilot etc.)
                </MenuItem>
                <MenuItem value="Vocational">Vocational</MenuItem>
                <MenuItem value="--Other--">--Other--</MenuItem>
              </FormSelect>
            </FormGroupComponent>

            {formData.fieldOfStudy === "--Other--" && (
              <Box sx={{ mt: 2 }}>
                <FormInput
                  label="Other"
                  id="fieldOfStudyOther"
                  value={formData.fieldOfStudyOther}
                  onChange={handleChange("fieldOfStudyOther")}
                  required
                  disabled={isReadOnly}
                  error={Boolean(stepErrors.fieldOfStudyOther)}
                  helperText={stepErrors.fieldOfStudyOther || ""}
                  status={getStatus(
                    "fieldOfStudyOther",
                    formData.fieldOfStudy === "--Other--",
                  )}
                />
              </Box>
            )}
          </Grid>
        </FormRow>

        <Divider />

        {/* Program name */}
        <FormRow spacing={2}>
          <Grid item xs={12}>
            <FormGroupComponent
              label="Please name program you plan to enroll in:"
              className="form-group-row"
            >
              <FormInput
                label="Please name program you plan to enroll in:"
                id="PS_Program_Name"
                value={formData.PS_Program_Name}
                onChange={handleChange("PS_Program_Name")}
                required
                disabled={isReadOnly}
                error={Boolean(stepErrors.PS_Program_Name)}
                helperText={stepErrors.PS_Program_Name || ""}
                status={getStatus("PS_Program_Name", true)}
              />
            </FormGroupComponent>
          </Grid>
        </FormRow>

        {/* Other plans */}
        <FormRow spacing={2}>
          <Grid item xs={12}>
            <FormGroupComponent
              label="Other educational plans:"
              className="form-group-row"
            >
              <FormInput
                label="Other educational plans:"
                id="otherEducationPlans"
                value={formData.otherEducationPlans}
                onChange={handleChange("otherEducationPlans")}
                multiline
                rows={4}
                disabled={isReadOnly}
                error={Boolean(stepErrors.otherEducationPlans)}
                helperText={stepErrors.otherEducationPlans || ""}
                status={getStatus("otherEducationPlans", false)}
              />
            </FormGroupComponent>
          </Grid>
        </FormRow>

        <Divider sx={{ my: 3 }} />

        {/* Certification */}
        <Stack spacing={1} sx={{ textAlign: "center" }}>
          <Typography variant="body1" fontWeight={800}>
            INFORMATION ON THIS APPLICATION IS CERTIFIED CORRECT
          </Typography>

          <Typography
            variant="body2"
            sx={{ fontStyle: "italic", color: "text.secondary" }}
          >
            By dating and typing your name you certify that all the infomation
            you have provided is accurate and correct to the best of your
            knowledge.
          </Typography>
        </Stack>

        <Divider sx={{ my: 3 }} />

        <FormRow spacing={2}>
          <Grid item xs={12} md={6}>
            <FormGroupComponent label="Date:" className="form-group-column">
              <FormInput
                label="Date"
                id="academicSignedDate"
                type="date"
                value={formData.academicSignedDate}
                onChange={handleChange("academicSignedDate")}
                required
                disabled={isReadOnly}
                error={Boolean(stepErrors.academicSignedDate)}
                helperText={stepErrors.academicSignedDate || ""}
                status={getStatus("academicSignedDate", true)}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </FormGroupComponent>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormGroupComponent
              label="Type Your Name:"
              className="form-group-column"
            >
              <FormInput
                label="Type Your Name:"
                id="academicSignedName"
                value={formData.academicSignedName}
                onChange={handleChange("academicSignedName")}
                required
                disabled={isReadOnly}
                error={Boolean(stepErrors.academicSignedName)}
                helperText={stepErrors.academicSignedName || ""}
                status={getStatus("academicSignedName", true)}
              />
            </FormGroupComponent>
          </Grid>
        </FormRow>
      </Stack>
    </Paper>
  );
}

export default memo(StepContent2);
